<template>
	<div>
		<Loader v-model="loading"></Loader>

		<!-- Main Content -->
		<div class="container mt-4">
			<!-- Profile Block -->
			<div class="card shadow-sm overflow-hidden mb-4">
				<!-- Profile Cover with Avatar and Stats -->
				<div class="bg-purple position-relative" style="height: 280px; background-color: #6f42c1;">
					<!-- Avatar -->
					<div class="text-center position-relative" style="padding-top: 60px;">
						<div class="d-inline-block position-relative rounded-circle overflow-hidden cursor-pointer bg-white" 
							style="width: 120px; height: 120px; border: 4px solid white; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);" 
							@click="triggerFileInput">
							<img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" class="w-100 h-100 object-fit-cover">
							<i v-else class="fas fa-user-circle fa-5x"></i>
							<div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center opacity-0 hover-opacity-100 transition-opacity" 
								style="background: rgba(0, 0, 0, 0.5); transition: opacity 0.3s;">
								<i class="fas fa-camera text-white" style="font-size: 1.5rem;"></i>
							</div>
						</div>
						<input
							type="file"
							ref="fileInput"
							@change="handleFileChange"
							accept="image/*"
							class="d-none"
						>
					</div>
					
					<!-- Stats in Cover -->
					<div class="d-flex justify-content-center position-absolute w-100" style="bottom: 20px;">
						<div v-if="user?.has_ndf" class="mx-4 text-center text-white">
							<div class="fw-bold fs-4">{{ expensesCount }}</div>
							<div class="small">Notes de frais</div>
						</div>
						<div class="mx-4 text-center text-white">
							<div class="fw-bold fs-4">{{ totalLeaveDays }}</div>
							<div class="small">Jours pris</div>
						</div>
						<div class="mx-4 text-center text-white">
							<div class="fw-bold fs-4">{{ pendingDays }}</div>
							<div class="small">Jours en attente</div>
						</div>
					</div>
				</div>
				<!-- Profile Content -->
				<div class="bg-white px-3 py-4">
					<div class="row">
						<!-- Profile Info Column -->
						<div class="col-md-6 border-end">
							<div class="pe-md-4">
								<h5 class="mb-4">Informations personnelles</h5>
								<n-form :model="user">
									<n-form-item label="Nom" path="name">
										<n-input v-model:value="formData.name" placeholder="Votre nom"/>
									</n-form-item>
									<n-form-item label="Notes de frais" path="has_ndf">
										<n-switch v-model:value="formData.has_ndf">
											<template #checked>Activé</template>
											<template #unchecked>Désactivé</template>
										</n-switch>
									</n-form-item>
									<n-form-item label="Mode de paiement par défaut" path="default_payment_method">
										<n-select v-model:value="formData.default_payment_method" :options="paymentMethodOptions" placeholder="Aucun"/>
									</n-form-item>
								</n-form>

								<div class="mt-4 d-flex justify-content-end">
									<n-button type="primary" @click="submit" :loading="loading">
										<template #icon>
											<i class="fas fa-save"></i>
										</template>
										Enregistrer
									</n-button>
								</div>
							</div>
						</div>
						
						<!-- Activity Feed Column -->
						<div class="col-md-6">
							<div class="ps-md-4 h-100 d-flex flex-column">
								<h5 class="mb-3">Fil d'activités</h5>
								<div v-if="activities.length === 0" class="text-center p-3">
									<p class="text-muted">Aucune activité récente</p>
								</div>
								<div v-else class="timeline-container overflow-auto" style="max-height: 400px;">
									<div v-for="(activity, index) in activities" :key="index" class="d-flex mb-3 align-items-start">
										<div class="me-3 flex-shrink-0">
											<div class="rounded-circle d-flex align-items-center justify-content-center" 
												style="width: 40px; height: 40px;" :class="getActivityIconClass(activity.type)">
												<i v-if="activity.type === 'leave_request'" class="fas fa-calendar-alt text-white"></i>
												<i v-else-if="activity.type === 'leave_validated'"
												   class="fas fa-check-circle text-white"></i>
												<i v-else-if="activity.type === 'leave_refused'"
												   class="fas fa-times-circle text-white"></i>
												<i v-else-if="activity.type === 'leave_cancelled'" class="fas fa-ban text-white"></i>
												<i v-else-if="activity.type === 'expense_created'"
												   class="fas fa-receipt text-white"></i>
												<i v-else-if="activity.type === 'expense_validated'"
												   class="fas fa-check-circle text-white"></i>
												<i v-else-if="activity.type === 'expense_refused'"
												   class="fas fa-times-circle text-white"></i>
												<i v-else class="fas fa-info-circle text-white"></i>
											</div>
										</div>
										<div class="flex-grow-1">
											<div class="fw-medium mb-1" v-html="activity.title"></div>
											<div class="text-secondary small">{{ formatActivityDate(activity.date) }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import {ref, computed} from 'vue';
import {useStore} from 'vuex';
import {useMessage} from 'naive-ui';
import Loader from "../Loader.vue";

const store = useStore();
const message = useMessage();
const loading = ref(null);
const user = ref({});
const formData = ref({});
const fileInput = ref(null);
const newAvatar = ref(null);
const previewAvatar = ref(null);

const paymentMethodOptions = [
	{label: 'Perso', value: 'Perso'},
	{label: 'CB BIL', value: 'CB BIL'},
	{label: 'CB BPLC', value: 'CB BPLC'},
];

// Import date-fns for date calculations
import {differenceInDays, parseISO, formatDistance, format} from 'date-fns';
import {fr} from 'date-fns/locale';

// Stats data
const leaves = computed(() => store.getters.getLeaves || []);

// Calculate total days of validated leave requests
const totalLeaveDays = computed(() => {
	return leaves.value.reduce((total, leave) => {
		// Only count validated leaves
		if (leave.status !== 'validated') return total;

		// Calculate days between start and end dates (inclusive)
		const days = differenceInDays(parseISO(leave.end), parseISO(leave.start)) + 1;
		return total + days;
	}, 0);
});

// Calculate days of pending leave requests
const pendingDays = computed(() => {
	return leaves.value.filter(leave => leave.status === 'pending')
		.reduce((total, leave) => {
			// Calculate days between start and end dates (inclusive)
			const days = differenceInDays(parseISO(leave.end), parseISO(leave.start)) + 1;
			return total + days;
		}, 0);
});

const expensesCount = ref(0);

// Activity feed data
const activities = ref([]);

// Function to get activity icon based on type
function getActivityIcon(type) {
	switch (type) {
		case 'leave_request':
			return 'fas fa-calendar-plus';
		case 'leave_validated':
			return 'fas fa-calendar-check';
		case 'leave_cancelled':
			return 'fas fa-calendar-times';
		case 'leave_refused':
			return 'fas fa-calendar-minus';
		case 'expense_new':
			return 'fas fa-receipt';
		case 'expense_validated':
			return 'fas fa-check-circle';
		case 'expense_refused':
			return 'fas fa-times-circle';
		default:
			return 'fas fa-bell';
	}
}

// Function to get activity icon class based on type
function getActivityIconClass(type) {
	switch (type) {
		case 'leave_request':
			return 'leave-request';
		case 'leave_validated':
			return 'leave-validated';
		case 'leave_refused':
			return 'leave-refused';
		case 'leave_cancelled':
			return 'leave-cancelled';
		case 'expense_created':
			return 'expense-created';
		case 'expense_validated':
			return 'expense-validated';
		case 'expense_refused':
			return 'expense-refused';
		default:
			return 'info';
	}
}

// Format activity date in a relative format (e.g., "il y a 2 heures")
function formatActivityDate(dateString) {
	try {
		const date = parseISO(dateString);
		let formattedDate = formatDistance(date, new Date(), {addSuffix: true, locale: fr});
		// Remove the word "environ" from the formatted date
		return formattedDate.replace('environ ', '');
	} catch (error) {
		console.error('Error formatting date:', error);
		return 'Date inconnue';
	}
}

// Fetch expenses count
async function fetchExpensesCount() {
	try {
		// Only fetch expenses if user has expense reports enabled
		if (!user.value?.has_ndf) {
			expensesCount.value = 0;
			return;
		}

		const pb = store.getters.getPb;
		if (!pb) return;

		const resultList = await pb.collection('expense').getList(1, 1, {
			filter: `user.id = "${user.value.id}" && deleted = false`,
		});

		expensesCount.value = resultList.totalItems;
	} catch (error) {
		console.error('Error fetching expenses count:', error);
	}
}

const avatarUrl = computed(() => {
	if (previewAvatar.value) {
		return previewAvatar.value;
	}
	if (!user.value?.id) return null;
	const pb = store.getters.getPb;
	return pb.files.getUrl(user.value, user.value.avatar, {'thumb': '200x200'});
});

async function loadUser() {
	loading.value = 'Chargement';
	try {
		await store.dispatch('setUser');
		user.value = store.getters.getUser;
		// Copier les valeurs dans formData
		formData.value = {
			name: user.value.name,
			has_ndf: user.value.has_ndf,
			default_payment_method: user.value.default_payment_method
		};

		// Fetch additional data for stats
		await store.dispatch('fetchLeaves');
		await fetchExpensesCount();

		// Load activity feed using the already fetched leaves data
		await loadActivities();
	} catch (error) {
		message.error('Erreur lors du chargement du profil');
	} finally {
		loading.value = false;
	}
}

// Load activities for the feed
async function loadActivities() {
	try {
		const pb = store.getters.getPb;
		if (!pb || !user.value) return;

		const userId = user.value.id;
		const tempActivities = [];

		// Use the already fetched leaves data from the computed property
		const allLeaves = leaves.value;
		console.log('DEBUG - All leaves:', allLeaves);
		console.log('DEBUG - Current user ID:', userId);

		// Include all leaves for now to debug
		const userLeaves = allLeaves;
		console.log('DEBUG - User leaves:', userLeaves);


		// Add leave-related activities
		userLeaves.forEach(leave => {
			try {
				// Check if start and end dates are valid
				if (!leave.start || !leave.end) {
					console.warn('Leave missing start or end date:', leave);
					return;
				}

				// Calculate days and format dates
				const days = differenceInDays(parseISO(leave.end), parseISO(leave.start)) + 1;
				const formattedStartDate = format(parseISO(leave.start), 'dd/MM/yyyy', {locale: fr});
				const formattedEndDate = format(parseISO(leave.end), 'dd/MM/yyyy', {locale: fr});

				// New leave request
				tempActivities.push({
					type: 'leave_request',
					title: `Vous avez demandé <strong>${days} jour${days > 1 ? 's' : ''}</strong> de congés du <strong>${formattedStartDate}</strong> au <strong>${formattedEndDate}</strong>`,
					date: leave.created
				});

				// Leave validated
				if (leave.status === 'validated') {
					tempActivities.push({
						type: 'leave_validated',
						title: `Votre demande de congés du <strong>${formattedStartDate}</strong> au <strong>${formattedEndDate}</strong> a été <strong>validée</strong>`,
						date: leave.validated || leave.updated || leave.created
					});
				}

				// Leave refused
				if (leave.status === 'refuelling') {
					tempActivities.push({
						type: 'leave_refused',
						title: `Votre demande de congés du <strong>${formattedStartDate}</strong> au <strong>${formattedEndDate}</strong> a été <strong>refusée</strong>`,
						date: leave.validated || leave.updated || leave.created
					});
				}

				// Leave cancelled
				if (leave.status === 'cancelled') {
					tempActivities.push({
						type: 'leave_cancelled',
						title: `Vous avez <strong>annulé</strong> votre demande de congés du <strong>${formattedStartDate}</strong> au <strong>${formattedEndDate}</strong>`,
						date: leave.updated || leave.created
					});
				}
			} catch (error) {
				console.error('Error processing leave activity:', error);
			}
		});

		// Get expense reports if user has expense feature enabled
		if (user.value.has_ndf) {
			try {
				// Try different filter formats to see which one works
				let expensesList = [];
				try {
					expensesList = await pb.collection('expense').getFullList({
						filter: `user = "${userId}" && deleted = false`,
						sort: '-created',
						expand: 'user,validator'
					});
					console.log('DEBUG - Expenses with user=:', expensesList);
				} catch (err) {
					console.error('Error with first filter format:', err);

					try {
						expensesList = await pb.collection('expense').getFullList({
							filter: `user.id = "${userId}" && deleted = false`,
							sort: '-created',
							expand: 'user,validator'
						});
						console.log('DEBUG - Expenses with user.id=:', expensesList);
					} catch (err2) {
						console.error('Error with second filter format:', err2);
					}
				}

				// Add expense-related activities
				expensesList.forEach(expense => {
					try {
						// New expense
						tempActivities.push({
							type: 'expense_created',
							title: `Vous avez créé une note de frais de <strong>${expense.amount} €</strong>`,
							date: expense.created
						});

						// Expense validated
						if (expense.status === 'validated') {
							tempActivities.push({
								type: 'expense_validated',
								title: `Votre note de frais de <strong>${expense.amount} €</strong> a été <strong>validée</strong>`,
								date: expense.validate_at || expense.updated
							});
						}

						// Expense refused
						if (expense.status === 'refused') {
							tempActivities.push({
								type: 'expense_refused',
								title: `Votre note de frais de <strong>${expense.amount} €</strong> a été <strong>refusée</strong>`,
								date: expense.refuse_at || expense.updated
							});
						}
					} catch (err) {
						console.error('Error processing expense:', err, expense);
					}
				});
			} catch (err) {
				console.error('Error fetching expenses:', err);
			}
		}

		// Sort activities by date (newest first)
		tempActivities.sort((a, b) => new Date(b.date) - new Date(a.date));

		// Limit to 15 most recent activities
		activities.value = tempActivities.slice(0, 15);
		console.log('DEBUG - Final activities:', activities.value);

		// If no activities were found, add a test activity to verify the display works
		if (activities.value.length === 0) {
			activities.value = [{
				type: 'leave_request',
				title: 'Activité de test pour déboguer l\'affichage',
				date: new Date().toISOString()
			}];
			console.log('DEBUG - Added test activity');
		}
	} catch (error) {
		console.error('Error loading activities:', error);
		activities.value = [];
	}
}

function triggerFileInput() {
	fileInput.value.click();
}

async function handleFileChange(event) {
	const file = event.target.files[0];
	if (!file) return;

	// Vérifier le type de fichier
	if (!file.type.startsWith('image/')) {
		message.error('Veuillez sélectionner une image');
		return;
	}

	// Vérifier la taille du fichier (max 5MB)
	if (file.size > 5 * 1024 * 1024) {
		message.error('L\'image ne doit pas dépasser 5MB');
		return;
	}

	newAvatar.value = file;

	// Créer une URL pour la prévisualisation
	previewAvatar.value = URL.createObjectURL(file);
}

async function submit() {
	loading.value = 'Chargement';
	try {
		if (newAvatar.value) {
			const payload = new FormData();
			payload.append('avatar', newAvatar.value);
			payload.append('name', formData.value.name);
			payload.append('has_ndf', formData.value.has_ndf);
			payload.append('default_payment_method', formData.value.default_payment_method || '');
			await store.dispatch('setUser', payload);
		} else {
			// Mise à jour de l'utilisateur sans avatar
			await store.dispatch('setUser', formData.value);
		}
		message.success('Profil mis à jour avec succès');
		// Réinitialiser l'input file et la prévisualisation
		if (fileInput.value) {
			fileInput.value.value = '';
		}
		newAvatar.value = null;
		previewAvatar.value = null;
		// Recharger les données de l'utilisateur
		await loadUser();
	} catch (error) {
		console.log(error);
		message.error('Erreur lors de la mise à jour du profil');
	} finally {
		loading.value = false;
	}
}

loadUser();
</script>

<style scoped>
#content {
	max-height: 90vh;
}

.profile-content {
	padding: 1rem;
}

.profile-header {
	padding: 0;
	margin-bottom: 2rem;
}

/* Stats cards styles */
.stat-card {
	height: 100%;
	transition: all 0.3s ease;
}

.stat-card:hover {
	transform: translateY(-5px);
	box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.stat-content {
	display: flex;
	align-items: center;
}

.stat-icon {
	width: 50px;
	height: 50px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	margin-right: 15px;
	font-size: 1.5rem;
	transition: all 0.3s ease;
	color: #fff;
}

.stat-card:hover .stat-icon {
	transform: scale(1.1);
}

/* Soft color styles inspired by Categories.vue */
.fuel {
	background-color: #e6f0ff;
	color: #0d6efd;
}

.restaurant {
	background-color: #e6fff0;
	color: #198754;
}

.restaurant-client {
	background-color: #fff8e6;
	color: #fd7e14;
}

.misc {
	background-color: #f0f0f0;
	color: #6c757d;
}

.purple {
	background-color: #f3e6ff;
	color: #6f42c1;
}

.transport {
	background-color: #e6f9ff;
	color: #0dcaf0;
}

.stat-info {
	flex: 1;
}

.stat-value {
	font-size: 1.5rem;
	font-weight: bold;
	margin: 0;
}

.stat-label {
	margin: 0;
	color: #6c757d;
}

/* Activity Feed Styles */
.activity-feed-card {
	max-height: 600px;
	overflow-y: auto;
}

.timeline-container {
	position: relative;
	padding-left: 10px;
}

.timeline-container::before {
	content: '';
	position: absolute;
	left: 23px;
	top: 0;
	height: 100%;
	width: 1px;
	background-color: #e0e0e0;
	z-index: 1;
}

.timeline-item {
	display: flex;
	align-items: flex-start;
	margin-bottom: 15px;
	position: relative;
	z-index: 2;
}

.timeline-icon-container {
	position: relative;
	margin-right: 10px;
	z-index: 2;
}

.timeline-icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 0.9rem;
	color: white;
	position: relative;
	z-index: 3;
	transition: transform 0.2s;
}

.timeline-item:hover .timeline-icon {
	transform: scale(1.1);
}

.timeline-content {
	flex: 1;
	background-color: #f8f9fa;
	border-radius: 6px;
	padding: 10px 12px;
	transition: transform 0.2s;
	margin-bottom: 0;
}

.timeline-item:hover .timeline-content {
	transform: translateX(5px);
}

.timeline-title {
	font-weight: 500;
	margin-bottom: 3px;
	font-size: 0.95rem;
}

.timeline-date {
	font-size: 0.8rem;
	color: var(--bs-secondary);
}

/* Styles pour les icônes d'activité - convertis en classes Bootstrap dans le HTML */
.leave-request {
	background-color: #e6f0ff !important;
}
.leave-request i {
	color: #0d6efd !important;
}

.leave-validated,
.expense-validated {
	background-color: #e6fff0 !important;
}
.leave-validated i,
.expense-validated i {
	color: #198754 !important;
}

.leave-refused,
.expense-refused {
	background-color: #ffe6e6 !important;
}
.leave-refused i,
.expense-refused i {
	color: #dc3545 !important;
}

.leave-cancelled {
	background-color: #f0f0f0 !important;
}
.leave-cancelled i {
	color: #6c757d !important;
}

.expense-created {
	background-color: #f3e6ff !important;
}
.expense-created i {
	color: #6f42c1 !important;
}

.hover-opacity-100:hover {
	opacity: 1 !important;
}

.transition-opacity {
	transition: opacity 0.3s;
}

.cursor-pointer {
	cursor: pointer;
}

.object-fit-cover {
	object-fit: cover;
}

:deep(.n-form-item) {
	margin-bottom: 1.5rem;
}

:deep(.n-button) {
	margin-left: 1rem;
}
</style>